import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";

type Issue = {
  field: string;
  label: string;
  cause: string;
  correction: string;
};
type Listing = {
  id: string;
  status: string;
  external_reference: string | null;
  provider_url: string | null;
  withdrawal_reason: string | null;
  updated_at: string;
};
type EventRow = {
  id: number;
  event_type: string;
  summary: string;
  created_at: string;
};
type Revision = {
  id: number;
  version_number: number;
  action: string;
  created_at: string;
};
type Panel = {
  adapter: { name: string; mode: string; enabled: boolean };
  listing: Listing | null;
  issues: Issue[];
  history: EventRow[];
  revisions: Revision[];
  can_manage: boolean;
};

const statusLabels: Record<string, string> = {
  draft: "Rascunho",
  validation_failed: "Precisa de correção",
  ready: "Pronto para publicar",
  published: "Publicado",
  update_required: "Atualização necessária",
  withdrawn: "Retirado",
  error: "Erro",
};
const actionLabels: Record<string, string> = {
  publish: "Publicação",
  update: "Atualização",
  republish: "Republicação",
};
const readable = (value: string) =>
  new Intl.DateTimeFormat("pt-PT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));

export function VehicleChannelPublication({
  vehicleId,
}: {
  vehicleId: string;
}) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("vehicle_channel_panel", {
      p_vehicle_id: vehicleId,
    });
    setLoading(false);
    if (error) return setNotice({ kind: "error", text: error.message });
    setPanel(data as Panel);
  };
  useEffect(() => {
    void load();
  }, [vehicleId]);

  const operate = async (action: string, reason: string | null = null) => {
    setBusy(true);
    setNotice(null);
    const { data, error } = await supabase.rpc("operate_vehicle_channel", {
      p_vehicle_id: vehicleId,
      p_action: action,
      p_reason: reason,
    });
    setBusy(false);
    if (error) return setNotice({ kind: "error", text: error.message });
    const next = data as Panel;
    setPanel(next);
    if (action === "validate")
      setNotice({
        kind: next.issues.length ? "error" : "success",
        text: next.issues.length
          ? "A validação encontrou dados que precisam de correção."
          : "Anúncio validado e pronto para publicar.",
      });
    else
      setNotice({
        kind: "success",
        text:
          action === "withdraw"
            ? "Anúncio retirado do simulador."
            : "Anúncio guardado no simulador Standvirtual.",
      });
  };
  const withdraw = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await operate("withdraw", String(form.get("reason") ?? ""));
    setWithdrawOpen(false);
  };

  if (loading)
    return (
      <section className="channel-publication">
        <p>A preparar a publicação multicanal…</p>
      </section>
    );
  if (!panel)
    return (
      <section className="channel-publication">
        <p>Não foi possível carregar a publicação multicanal.</p>
      </section>
    );
  const status = panel.listing?.status ?? "draft";
  const canPublish =
    panel.can_manage && panel.adapter.enabled && panel.issues.length === 0;
  return (
    <section className="channel-publication">
      <header>
        <div>
          <p className="eyebrow">Publicação multicanal</p>
          <h3>{panel.adapter.name} — Simulador de Testes</h3>
          <p>
            Teste o ciclo completo do anúncio sem o enviar para um portal real.
            Os dados publicados vêm sempre da ficha do Stand.
          </p>
        </div>
        <span className={`channel-status ${status}`}>
          {statusLabels[status] ?? status}
        </span>
      </header>
      {notice && (
        <button
          type="button"
          className={`marketing-feedback ${notice.kind}`}
          onClick={() => setNotice(null)}
        >
          {notice.text} ×
        </button>
      )}
      <div className="channel-actions">
        {panel.can_manage && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void operate("validate")}
          >
            Validar anúncio
          </button>
        )}
        {panel.can_manage &&
          status !== "published" &&
          status !== "update_required" &&
          status !== "withdrawn" && (
            <button
              type="button"
              className="stock-primary"
              disabled={busy || !canPublish}
              onClick={() => void operate("publish")}
            >
              Publicar
            </button>
          )}
        {panel.can_manage && status === "update_required" && (
          <button
            type="button"
            className="stock-primary"
            disabled={busy || !canPublish}
            onClick={() => void operate("publish")}
          >
            Atualizar anúncio
          </button>
        )}
        {panel.can_manage && status === "withdrawn" && (
          <button
            type="button"
            className="stock-primary"
            disabled={busy || !canPublish}
            onClick={() => void operate("republish")}
          >
            Republicar
          </button>
        )}
        {panel.can_manage &&
          (status === "published" || status === "update_required") && (
            <button
              type="button"
              className="channel-danger"
              disabled={busy}
              onClick={() => setWithdrawOpen(true)}
            >
              Retirar anúncio
            </button>
          )}
      </div>
      {panel.listing?.external_reference && (
        <div className="channel-reference">
          <span>Referência simulada</span>
          <strong>{panel.listing.external_reference}</strong>
        </div>
      )}
      {panel.issues.length > 0 && (
        <div className="channel-issues">
          <h4>Dados a corrigir</h4>
          {panel.issues.map((issue) => (
            <article key={issue.field}>
              <strong>{issue.label}</strong>
              <p>{issue.cause}</p>
              <small>{issue.correction}</small>
            </article>
          ))}
        </div>
      )}
      <div className="channel-history-grid">
        <section>
          <h4>Histórico</h4>
          {panel.history.length === 0 ? (
            <p className="channel-empty">Ainda não existem operações.</p>
          ) : (
            panel.history.map((item) => (
              <article key={item.id}>
                <strong>{item.summary}</strong>
                <small>{readable(item.created_at)}</small>
              </article>
            ))
          )}
        </section>
        <section>
          <h4>Versões enviadas</h4>
          {panel.revisions.length === 0 ? (
            <p className="channel-empty">Nenhuma versão publicada.</p>
          ) : (
            panel.revisions.map((item) => (
              <article key={item.id}>
                <strong>
                  Versão {item.version_number} ·{" "}
                  {actionLabels[item.action] ?? item.action}
                </strong>
                <small>{readable(item.created_at)}</small>
              </article>
            ))
          )}
        </section>
      </div>
      {withdrawOpen && (
        <div className="marketing-dialog-backdrop">
          <form
            className="marketing-dialog channel-withdraw"
            onSubmit={withdraw}
          >
            <header>
              <div>
                <p className="eyebrow">Retirar anúncio</p>
                <h3>Indique o motivo</h3>
              </div>
              <button type="button" onClick={() => setWithdrawOpen(false)}>
                ×
              </button>
            </header>
            <div>
              <label className="wide">
                <span>Motivo *</span>
                <textarea
                  name="reason"
                  rows={4}
                  minLength={3}
                  required
                  autoFocus
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setWithdrawOpen(false)}>
                Cancelar
              </button>
              <button className="stock-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
